import React, { useEffect, useState } from 'react';
import apiService from '../apiService';
import { loadCriteria, tagMeta, stars, fmtDate, CONTEXT_LABELS, TARGET_LABELS, STATUS_META } from '../utils/reviewLabels';

/**
 * Avis reçus par une cible (fiche chauffeur, client, commerce…) : résumé + critères + liste.
 * type = 'driver' (toutes activités du chauffeur) | 'client' | 'supplier' | 'lodging' | …
 */
export default function TargetReviews({ type, id, profile }) {
  const [data, setData] = useState(null);
  const [criteria, setCriteria] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiService.get(`/reviews/admin/target/${type}/${encodeURIComponent(id)}?limit=50`, { userType: 'admin' }).catch(() => null),
      loadCriteria(),
    ]).then(([res, crit]) => {
      if (!alive) return;
      setData(res && res.success ? res : { reviews: [], stats: { average: null, count: 0, distribution: {}, tags: [], low_count: 0 } });
      setCriteria(crit);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [type, id]);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: '#5C636E' }}>Chargement des avis…</div>;
  const st = data.stats || {};
  const minCount = criteria?.min_count_display ?? 5;
  const isNew = (st.count || 0) < minCount;
  const penalty = profile?.cancellation_stats?.penalty || 0;
  const dist = st.distribution || {};
  const maxDist = Math.max(1, ...Object.values(dist));

  return (
    <div>
      {/* Résumé */}
      <div style={{ padding: '1rem', borderRadius: 12, marginBottom: 12, background: 'linear-gradient(135deg, #fef3c7, #fde68a)', border: '1px solid #FFB900', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center', minWidth: 110 }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#C9350F' }}>⭐ {st.average != null ? st.average.toFixed(2) : '—'}</div>
          <div style={{ fontSize: '0.8rem', color: '#78350f' }}>{st.count || 0} avis{isNew ? ' · Nouveau' : ''}</div>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          {[5, 4, 3, 2, 1].map(n => (
            <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#78350f' }}>
              <span style={{ width: 10 }}>{n}</span>
              <div style={{ flex: 1, height: 6, background: '#fff7e0', borderRadius: 3 }}><div style={{ width: `${((dist[n] || 0) / maxDist) * 100}%`, height: 6, background: n <= 2 ? '#DC2626' : '#FFB900', borderRadius: 3 }} /></div>
              <span style={{ width: 22, textAlign: 'right' }}>{dist[n] || 0}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: '0.78rem', color: '#78350f', minWidth: 140 }}>
          {profile?.rating != null && <div>Note affichée : <strong>{Number(profile.rating).toFixed(2)}</strong></div>}
          {penalty > 0 && <div style={{ color: '#991B1B' }}>Pénalité annulations : −{penalty}</div>}
          {profile?.rating_taxi?.count > 0 && <div>Courses : {profile.rating_taxi.avg} ({profile.rating_taxi.count})</div>}
          {profile?.rating_delivery?.count > 0 && <div>Livraisons : {profile.rating_delivery.avg} ({profile.rating_delivery.count})</div>}
          {profile?.rating_rental?.count > 0 && <div>Locations : {profile.rating_rental.avg} ({profile.rating_rental.count})</div>}
          {profile?.rating_watch?.active && <div style={{ color: '#B45309', fontWeight: 700 }}>⚠ À surveiller ({profile.rating_watch.low_count} notes ≤ 2)</div>}
          {st.low_count > 0 && !profile?.rating_watch?.active && <div>Notes ≤ 2 : {st.low_count}</div>}
        </div>
      </div>

      {/* Critères les plus cités */}
      {st.tags?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {st.tags.map(t => {
            const m = tagMeta(criteria?.map, t.target_type, t.key);
            const pos = t.positive ?? m.positive;
            return (
              <span key={`${t.target_type}:${t.key}`} style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.74rem', fontWeight: 600, background: pos ? '#ECFDF5' : '#FEE2E2', color: pos ? '#166534' : '#991B1B' }}>
                {m.label} · {t.count}
              </span>
            );
          })}
        </div>
      )}

      {/* Liste */}
      {data.reviews.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#9AA0A8', padding: '1rem', fontSize: '0.85rem' }}>Aucun avis pour le moment</p>
      ) : (
        <div style={{ maxHeight: 350, overflowY: 'auto' }}>
          {data.reviews.map(r => {
            const sm = STATUS_META[r.moderation_status] || STATUS_META.published;
            return (
              <div key={r.review_id} style={{ padding: 12, borderBottom: '1px solid #F4F5F7', fontSize: '0.85rem', opacity: r.moderation_status === 'published' ? 1 : 0.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                  <span style={{ fontWeight: 600, color: r.rating <= 2 ? '#991B1B' : '#1C1C1C' }}>{stars(r.rating)} ({r.rating}/5) <span style={{ color: '#9AA0A8', fontWeight: 400 }}>· {CONTEXT_LABELS[r.context_type] || r.context_type}{type === 'driver' && r.target_type !== 'driver_taxi' ? ` · ${TARGET_LABELS[r.target_type]}` : ''}</span></span>
                  <span style={{ color: '#9AA0A8', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</span>
                </div>
                <div style={{ color: '#5C636E', fontSize: '0.8rem' }}>Par : {r.reviewer_name || (r.reviewer_type === 'driver' ? 'Chauffeur' : 'Client')}{r.reviewer_public_id ? ` (${r.reviewer_public_id})` : ''}{r.moderation_status !== 'published' && <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', background: sm.bg, color: sm.color }}>{sm.label}</span>}</div>
                {r.tags?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {r.tags.map(tag => { const m = tagMeta(criteria?.map, r.target_type, tag); return <span key={tag} style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', background: m.positive === false ? '#FEE2E2' : '#F4F5F7', color: m.positive === false ? '#991B1B' : '#414852' }}>{m.label}</span>; })}
                  </div>
                )}
                {r.comment && <div style={{ marginTop: 4, color: '#414852', fontStyle: 'italic', fontSize: '0.8rem' }}>« {r.comment} »</div>}
                {r.response?.text && <div style={{ marginTop: 4, color: '#166534', fontSize: '0.78rem' }}>↳ Réponse : {r.response.text}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
